import React, { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CheckIcon from '@mui/icons-material/Check';
import RefreshIcon from '@mui/icons-material/Refresh';
import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes';
import { CircularProgress } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import { GridFilterModel, GridSortModel, GridColDef } from '@mui/x-data-grid';

import CustomDataGrid from 'components/common/CustomDataGrid';
import { useGetClientsQuery } from 'services/api/clients.api';
import { Client } from 'services/types/client';

const last24hoursDate = new Date();
last24hoursDate.setDate(last24hoursDate.getDate() - 1);

const baseColumns: GridColDef<Client>[] = [
  {
    field: 'id',
    headerName: 'ID',
    width: 35,
  },
  {
    field: 'lastMessageDate',
    headerName: 'Hora',
    width: 70,
    valueFormatter: (value: Date) => {
      const dateValue = new Date(value);
      if (dateValue > last24hoursDate) return dateValue.toLocaleTimeString('es-CL');
      return `${dateValue.getDate()}/${dateValue.getMonth() + 1} ${dateValue.getHours()}:${dateValue
        .getMinutes()
        .toString()
        .padStart(2, '0')}`;
    },
  },
  {
    field: 'createdAt',
    headerName: '1er mensaje',
    width: 70,
    valueFormatter: (value: Date) => {
      const dateValue = new Date(value);
      if (dateValue > last24hoursDate) return dateValue.toLocaleTimeString('es-CL');
      return `${dateValue.getDate()}/${dateValue.getMonth() + 1} ${dateValue.getHours()}:${dateValue
        .getMinutes()
        .toString()
        .padStart(2, '0')}`;
    },
  },
  {
    field: 'name',
    headerName: 'Nombre',
    width: 150,
    renderCell(params) {
      return (
        <div style={{ color: params.row.isTestClient ? 'grey' : undefined, display: 'flex', alignItems: 'center' }}>
          {params.row.notes ? <SpeakerNotesIcon titleAccess={params.row.notes} sx={{ mr: 1 }} color="info" /> : null}
          {params.row.firstName} {params.row.lastName}
          {params.row.hasValidName && <CheckIcon color="success" sx={{ align: 'center' }} />}
        </div>
      );
    },
  },
  {
    field: 'notes',
    headerName: 'Notas',
    width: 160,
  },
  {
    field: 'source',
    headerName: 'Origen',
    width: 120,
  },
  {
    field: 'unreadMessagesCount',
    headerName: 'No leídos',
    width: 60,
    valueFormatter: (value: number) => (value > 0 ? value : ''),
  },
  {
    field: 'user',
    headerName: 'Vendedor asignado',
    width: 80,
    valueFormatter: (value?: { email: string }) => value?.email || '',
  },
];

const InsurexCustomClientsTable: FC = () => {
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(window.location.search);

  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [salespersonEmail, setSalespersonEmail] = useState<string>('');

  const recentlyAssignedSortModel: GridSortModel = [{ field: 'lastMessageDate', sort: 'asc' }];
  const pendingSortModel: GridSortModel = [{ field: 'lastMessageDate', sort: 'desc' }];

  const recentlyAssignedFilterModel: GridFilterModel = {
    items: [
      ...(salespersonEmail ? [{ field: 'user', operator: 'equals', value: salespersonEmail }] : []),
      { field: 'lastLeadStatus', operator: 'equals', value: 'ASSIGNED_TO_SALESMAN' },
    ],
  };

  const pendingFilterModel: GridFilterModel = {
    items: [
      ...(salespersonEmail ? [{ field: 'user', operator: 'equals', value: salespersonEmail }] : []),
      { field: 'unreadMessagesCount', operator: 'greaterThan', value: 0 },
      { field: 'user', operator: 'isNotEmpty', value: '' },
    ],
  };

  const paginationModel = { page: 0, pageSize: 20 };

  const {
    data: recentlyAssignedData,
    refetch: refetchRecentlyAssigned,
    isFetching: isFetchingRecentlyAssigned,
  } = useGetClientsQuery({
    paginationModel,
    filterModel: recentlyAssignedFilterModel,
    sortModel: recentlyAssignedSortModel,
    searchTerm: queryParams.get('searchTerm') || '',
  });

  const {
    data: pendingData,
    refetch: refetchPending,
    isFetching: isFetchingPending,
  } = useGetClientsQuery({
    paginationModel,
    filterModel: pendingFilterModel,
    sortModel: pendingSortModel,
    searchTerm: queryParams.get('searchTerm') || '',
  });

  const navigateToClientId = (id: number, newPage = false) => {
    queryParams.set('highlightedClientId', id.toString());
    if (newPage) {
      window.open(`/clients/${id}`, '_blank');
      return;
    }
    navigate(`?${queryParams.toString()}`, { replace: true });
    navigate(`/clients/${id}`);
  };

  const { clients: recentlyAssignedClients, total: recentlyAssignedTotal } = recentlyAssignedData || {
    clients: [],
    total: 0,
  };
  const { clients: pendingClients, total: pendingTotal } = pendingData || { clients: [], total: 0 };

  return (
    <Box
      sx={{
        width: '100%',
        height: '45%',
        minHeight: '45%',
        display: 'flex',
        flexDirection: 'column',
        mb: 4,
        flexShrink: 0,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 2 }}>
        <Tabs
          value={selectedTab}
          onChange={(_, newValue: number) => setSelectedTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Recién asignados sin atender ({recentlyAssignedTotal})
                {isFetchingRecentlyAssigned ? (
                  <CircularProgress size={16} />
                ) : (
                  <Tooltip title="Recargar">
                    <RefreshIcon
                      sx={{ cursor: 'pointer', fontSize: 16 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        refetchRecentlyAssigned().catch(() => {
                          // eslint-disable-next-line no-console
                          console.error('Error fetching recently assigned clients');
                        });
                      }}
                      color="disabled"
                    />
                  </Tooltip>
                )}
              </Box>
            }
            value={0}
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Con mensajes pendientes ({pendingTotal})
                {isFetchingPending ? (
                  <CircularProgress size={16} />
                ) : (
                  <Tooltip title="Recargar">
                    <RefreshIcon
                      sx={{ cursor: 'pointer', fontSize: 16 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        refetchPending().catch(() => {
                          // eslint-disable-next-line no-console
                          console.error('Error fetching pending clients');
                        });
                      }}
                      color="disabled"
                    />
                  </Tooltip>
                )}
              </Box>
            }
            value={1}
          />
        </Tabs>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="salesperson-select-label">Vendedor</InputLabel>
          <Select
            labelId="salesperson-select-label"
            value={salespersonEmail}
            label="Vendedor"
            onChange={(event) => setSalespersonEmail(event.target.value)}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="willianys.maneiro@mueveseguro.cl">Willianys</MenuItem>
            <MenuItem value="catalina.riffo@mueveseguro.cl">Catalina</MenuItem>
            <MenuItem value="romina.servela@mueveseguro.cl">Romina</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ minHeight: 0 }}>
        <CustomDataGrid<Client>
          rows={selectedTab === 0 ? recentlyAssignedClients : pendingClients}
          columns={baseColumns}
          filterModel={selectedTab === 0 ? recentlyAssignedFilterModel : pendingFilterModel}
          sortModel={selectedTab === 0 ? recentlyAssignedSortModel : pendingSortModel}
          onFilterModelChange={() => {}}
          onSortModelChange={() => {}}
          paginationModel={paginationModel}
          onPaginationModelChange={() => {}}
          rowCount={selectedTab === 0 ? recentlyAssignedTotal : pendingTotal}
          onRowClick={(params, event) => navigateToClientId(Number(params.id), event.ctrlKey || event.metaKey)}
          pageId="clients-table-insurex"
          getRowClassName={(params) => {
            if (params.id.toString() === queryParams.get('highlightedClientId')) return 'previous';
            return '';
          }}
          disableFilters
          disableSort
          disableColumnOrder
          disablePagination
          noResultsMessage={
            selectedTab === 0
              ? '¡Felicitaciones! No hay clientes recién asignados sin atender'
              : '¡Felicitaciones! No hay clientes con mensajes pendientes sin leer/responder'
          }
        />
      </Box>
    </Box>
  );
};

export default InsurexCustomClientsTable;
